import { Overlay, OverlayPositionBuilder, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Component, ComponentRef, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { combineLatest, EMPTY, forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, map, mergeMap, switchMap, take, takeUntil } from 'rxjs/operators';
import { memo } from '../../helpers/helper';
import { HallPass } from '../../models/HallPass';
import { ROLES, User } from '../../models/User';
import { HallPassesService } from '../../services/hall-passes.service';
import { KioskModeService } from '../../services/kiosk-mode.service';
import { UserService } from '../../services/user.service';
import { GroupsAndStudentSelectComponent } from '../../shared/shared-components/groups-and-student-select/groups-and-student-select.component';

@Component({
	selector: 'sp-student-typer',
	templateUrl: './student-typer.component.html',
	styleUrls: ['./student-typer.component.scss'],
})
export class StudentTyperComponent implements OnInit, OnDestroy {
	@Input()
	enableIdSearch: boolean;

	@Input()
	disabled: boolean;

	@Input()
	enableStudentSearch: boolean;

	@Input()
	enableScanning: boolean;

	@Input()
	inputFocused: boolean;

	@Input()
	tintColor: string;

	@Input()
	width: string;

	@Output()
	onSelectStudent = new EventEmitter<User>();

	@Output() blurEvent: EventEmitter<any> = new EventEmitter<any>();

	@Output() focusEvent: EventEmitter<boolean> = new EventEmitter<boolean>();

	@ViewChild('search')
	searchRef: ElementRef;

	constructor(
		private dialog: MatDialog,
		private kioskMode: KioskModeService,
		private userService: UserService,
		private passesService: HallPassesService,
		private positionBuilder: OverlayPositionBuilder,
		private overlay: Overlay
	) {}

	cardReaderValue = '';
	searchText: string;
	invalidId = {
		id: '',
		show: false,
	};

	searchedStudents: User[] = [];
	private searchText$ = new Subject<string>();
	private destroy$ = new Subject<void>();
	private overlayRef: OverlayRef;
	private studentSelectRef: ComponentRef<GroupsAndStudentSelectComponent>;
	isInputFocused = false;

	handleSelect(s: User) {
		if (this.studentSelectRef) {
			this.closeStudentSearchResults();
		}
		this.onSelectStudent.emit(s);
		this.searchedStudents = [];
		this.searchText = '';
	}

	ngOnInit(): void {
		if (this.enableScanning) {
			this.listenToKeyboardEvents();
		}

		this.searchText$
			.pipe(
				debounceTime(100),
				switchMap((searchText) => {
					let customIdUser: Observable<User | null>;
					if (this.enableIdSearch) {
						customIdUser = this.userService.possibleProfileByCustomId(searchText).pipe(
							map((r) => {
								if (!r?.results?.user) {
									return null;
								}
								if (r.results.user.length === 0) {
									return null;
								} else {
									return User.fromJSON(r.results.user[0]);
								}
							})
						);
					} else {
						customIdUser = of(null);
					}

					let studentSearch: Observable<User[]>;
					if (this.enableStudentSearch) {
						studentSearch = this.userService.searchProfile(ROLES.Student, 20, searchText).pipe(map((r) => r.results.map((u) => User.fromJSON(u))));
					} else {
						studentSearch = of([]);
					}
					return forkJoin([studentSearch.pipe(take(1)), customIdUser.pipe(take(1))]).pipe(
						catchError(() => {
							/**
							 * Not catching this error will cause the entire searchText Observable chain to error out and cancel.
							 * If this happens, the subscription to searchText will be closed and any incoming values into the
							 * searchText$ subject will be ignored
							 */
							return of<[User[], User]>([[], null]);
						})
					);
				}),
				takeUntil(this.destroy$)
			)
			.subscribe(([students, customIdUser]) => {
				if (customIdUser) {
					// Always prefer a custom id match
					this.searchedStudents = [customIdUser];
				} else {
					this.searchedStudents = students;
				}
				this.searchedStudents = this.searchedStudents || [];

				if (this.studentSelectRef) {
					this.studentSelectRef.instance.students = this.searchedStudents;
					if (this.searchedStudents.length > 0) {
						const overlayConfig = this.overlayRef.getConfig();
						overlayConfig.height = '400px';
						this.overlayRef.updateSize(overlayConfig);
						this.studentSelectRef.instance.maxHeight = '100%';
					} else {
						const overlayConfig = this.overlayRef.getConfig();
						overlayConfig.height = '168px';
						this.overlayRef.updateSize(overlayConfig);
						this.studentSelectRef.instance.maxHeight = '168px';
						this.studentSelectRef.instance.emptyStateString = 'Check your spelling and try again.';
					}
				}
				if (!this.studentSelectRef && this.searchedStudents.length > 0) {
					this.openStudentSearchResults(false);
				} else if (!this.studentSelectRef && this.searchedStudents.length === 0) {
					this.openStudentSearchResults(true);
				}
			});
	}

	getPlaceholder = memo((enableIdSearch: boolean, enableNameSearch: boolean) => {
		if (enableIdSearch && enableNameSearch) {
			return 'Type Your ID Number, Name, or Email';
		} else if (enableIdSearch) {
			return 'Type Your ID Number';
		} else {
			return 'Type Your Name or Email';
		}
	});

	onEnterKey() {
		if (this.searchedStudents.length != 1 && this.enableScanning) {
			this.tryScanning(this.searchText);
			this.searchText$.next('');
			return;
		} else if (this.searchedStudents.length !== 1) {
			return;
		}

		this.handleSelect(this.searchedStudents[0]);
	}

	private openStudentSearchResults(isEmpty = false): void {
		const positionStrategy = this.positionBuilder
			.flexibleConnectedTo(this.searchRef)
			// .withFlexibleDimensions(false)
			// .withPush(false)
			.withPositions([
				{
					originX: 'start',
					originY: 'bottom',
					overlayX: 'start',
					overlayY: 'top',
				},
			]);

		this.overlayRef = this.overlay.create({
			positionStrategy,
			hasBackdrop: false,
			width: this.searchRef.nativeElement.offsetWidth,
			height: isEmpty ? '168px' : '400px',
			panelClass: 'app-group-settings-container',
		});

		const studentSelectPortal = new ComponentPortal(GroupsAndStudentSelectComponent);
		this.studentSelectRef = this.overlayRef.attach(studentSelectPortal);
		this.studentSelectRef.location.nativeElement.style.margin = '12px 0 0 0';
		this.studentSelectRef.location.nativeElement.style.width = '100%';
		this.studentSelectRef.instance.groups = [];
		this.studentSelectRef.instance.canEditGroup = false;
		this.studentSelectRef.instance.students = isEmpty ? [] : this.searchedStudents;
		this.studentSelectRef.instance.emptyStateString = isEmpty ? 'Check your spelling and try again.' : '';
		this.studentSelectRef.instance.borderBottomRightRadius = '12px';
		this.studentSelectRef.instance.borderBottomLeftRadius = '12px';
		if (isEmpty) {
			this.studentSelectRef.instance.maxHeight = '168px';
		} else {
			this.studentSelectRef.instance.maxHeight = '100%';
		}

		this.studentSelectRef.instance.handleSelect.subscribe((student) => {
			this.handleSelect(student);
		});
	}

	closeStudentSearchResults() {
		this.overlayRef.detach();
		this.overlayRef = null;
		this.studentSelectRef = null;
	}

	@HostListener('document:click', ['$event'])
	onDocumentClick(event: MouseEvent): void {
		// for debugging: simulates a card reader event when document is clicked
		// let fake = new KeyboardEvent('keydown', { key: 'mhs03Enter' });
		// document.dispatchEvent(fake);

		if (this.overlayRef) {
			const clickTarget = event.target as HTMLElement;
			// Check if the click was outside the overlay
			if (!this.overlayRef.overlayElement.contains(clickTarget) && clickTarget !== this.searchRef.nativeElement) {
				this.closeStudentSearchResults();
			}
		}
	}
	listenToKeyboardEvents() {
		document.addEventListener('keydown', this.handleKeyboardEvents.bind(this));
	}
	handleKeyboardEvents(event: KeyboardEvent) {
		if (this.isInputFocused) {
			return;
		}
		if (event.key === 'Enter') {
			this.tryScanning(this.cardReaderValue);
			this.cardReaderValue = '';
		} else if (event.key.length === 1) {
			// Only read skip escape, ctrl, etc.
			this.cardReaderValue += event.key;
			if (this.cardReaderValue.length > 50) {
				this.cardReaderValue = '';
			}
		}
	}

	onBlur(): void {
		this.isInputFocused = false;
		this.inputFocused = false;
		const data = { string: this.searchText, showError: this.searchedStudents.length > 0 ? false : true };
		this.blurEvent.emit(data);
	}

	tryScanning(text: string) {
		if (text.trim() === '') {
			return;
		}
		this.userService
			.possibleProfileByCustomId(text.trim())
			.pipe(
				switchMap((customIdResponse) => {
					let { user } = customIdResponse.results;

					/**
					 * Why are we doing this filter?
					 *
					 * It seems the backend will return { results: { user: [null] } } if a user isn't found when searching by
					 * custom ID. Not really an ideal response from the backend, this will be patched very soon
					 */
					user = user.filter(Boolean);

					user = user.map((u) => User.fromJSON(u));

					if (user.length > 0) {
						return of(user[0]);
					}

					this.showIdNotFound(text, true);
					return EMPTY;
				}),
				mergeMap((user) => {
					return combineLatest([of(user), this.passesService.getActivePassesKioskMode(this.kioskMode.getCurrentRoom().value.id)]);
				}),
				map(([user, passes]) => {
					const myPass: HallPass = (passes as HallPass[]).find((pass: HallPass) => pass.student.id === user.id);
					if (myPass) {
						this.passesService
							.endPass(myPass.id)
							.toPromise()
							.then(() => {
								return of(null);
							});
					} else {
						this.handleSelect(user);
						return of(null);
					}
				})
			)
			.subscribe();
	}

	onSearch(searchText: string): void {
		if (searchText.trim().length === 0) {
			this.searchedStudents = [];
			if (this.studentSelectRef) {
				this.closeStudentSearchResults();
			}
			return;
		}
		this.searchText$.next(searchText.trim());
	}

	onFocus(): void {
		this.isInputFocused = true;
		this.inputFocused = true;
		this.focusEvent.emit(true);
		if (this.searchText && this.searchedStudents.length > 0 && !this.studentSelectRef) {
			this.openStudentSearchResults(false);
		} else if (this.searchText && this.searchedStudents.length === 0 && !this.studentSelectRef) {
			this.openStudentSearchResults(true);
		}
	}

	getValue(event: Event): string {
		return (event.target as HTMLInputElement).value;
	}

	private showIdNotFound(id: string, show: boolean): void {
		this.invalidId = { id, show };
		setTimeout(() => {
			this.invalidId = { id: '', show: false };
		}, 6000);
	}

	ngOnDestroy() {
		this.destroy$.next(undefined);
		this.destroy$.complete();
		this.overlayRef?.dispose();
	}
}
